import type { IAnvändare } from 'src/types/nya/common';
import type { NavGroupProps, NavItemDataProps, NavSectionProps } from 'src/components/nav-section';

// ----------------------------------------------------------------------

/**
 * Behörighetsgrupperna som sidebaren filtrerar på. Detta är medvetet ett annat
 * begrepp än backendens `RollNamn`: flera roller ger samma behörighet, och en
 * roll kan ge flera. Mappningen sker i {@link ROLL_TILL_BEHÖRIGHETER}.
 *
 * Typen är en union — inte `string` — så att en felstavning i `allowedRoles`
 * fångas av kompilatorn i stället för att tyst dölja en meny.
 */
export type Behörighet =
  | 'alla'
  | 'boende'
  | 'medlem'
  | 'anställd'
  | 'vårdnadshavare'
  | 'styrelseledamot'
  | 'organisation_admin'
  | 'ekonomi'
  | 'egenföretagare'
  | 'arborist'
  | 'revisor'
  | 'flinky_handläggare'
  | 'super_admin';

/**
 * Namngivna uppsättningar att använda i trädet i stället för att upprepa
 * samma arrayer. `BOENDE` räcker för allt som en vanlig medlem ska nå —
 * styrelse och admin ärver den behörigheten via mappningen nedan.
 */
export const BEHÖRIGHET = {
  ALLA: ['alla'],
  BOENDE: ['boende'],
  MEDLEM: ['medlem'],
  ANSTÄLLD: ['anställd'],
  VÅRDNADSHAVARE: ['vårdnadshavare'],
  STYRELSE: ['styrelseledamot'],
  EKONOMI: ['ekonomi'],
  ADMIN: ['organisation_admin'],
  KONSULT: ['egenföretagare'],
  ARBORIST: ['arborist'],
  REVISOR: ['revisor'],
  FLINKY_PERSONAL: ['flinky_handläggare'],
  SUPER_ADMIN: ['super_admin'],
} satisfies Record<string, Behörighet[]>;

/**
 * Navigeringsträdets posttyp. Identisk med {@link NavItemDataProps} så när som
 * på `allowedRoles`, som här är {@link Behörighet}[] i stället för `string[]` —
 * det är den smalare typen som gör att en felstavad behörighet inte kompilerar.
 * Trädet är strukturellt kompatibelt med `NavSectionProps['data']` åt andra
 * hållet, så nav-komponenterna behöver inte känna till den här typen.
 */
export type NavPost = Omit<NavItemDataProps, 'allowedRoles' | 'children'> & {
  allowedRoles?: Behörighet[];
  children?: NavPost[];
};

export type NavData = { subheader?: string; items: NavPost[] }[];

/**
 * Backendens roller (gemener) → behörighetsgrupper. Varje rad listar hela den
 * transitiva mängden, så uppslagningen aldrig behöver följa kedjor.
 *
 * Observera att styrelseroller också ger `boende`/`medlem`: en styrelseledamot i
 * en BRF är per definition medlem och ska nå tvättstuga och utlåningspool som
 * alla andra. `förvaltare` är undantaget — den bor inte i föreningen.
 */
const ROLL_TILL_BEHÖRIGHETER: Record<string, Behörighet[]> = {
  medlem: ['boende', 'medlem'],
  ekonomisk_förening_medlem: ['boende', 'medlem'],
  samfällighetsmedlem: ['boende', 'medlem'],
  blivande_medlem: ['boende'],
  hyresgäst: ['boende'],
  andrahandshyresgäst: ['boende'],
  // MEDVETET bara 'anställd', ingenting annat. En förskolas personal är inte medlemmar i
  // kooperativet och ska inte se föreningens boendeytor, ekonomi eller medlemsregister — bara sin
  // egen tid, frånvaro och sitt semestersaldo. Att lägga till 'boende' här hade öppnat Mitt
  // boende, utlåningspoolen och tvättstugan för all personal, i tysthet.
  anställd: ['anställd'],
  // Samma hållning som 'anställd', och av samma skäl: en vårdnadshavare är förälder i ett
  // kooperativ, inte boende i en förening. Rollen öppnar Mina barn och ingenting annat. Och den
  // räcker inte ens till det — backendens Vårdnadshavaruppslag härleder barnen ur hushållsnyckeln
  // i medlemsregistret, så den som inte matchar ett hushåll med barn ser en tom lista.
  vårdnadshavare: ['vårdnadshavare'],
  styrelsemedlem: ['boende', 'medlem', 'styrelseledamot', 'ekonomi'],
  styrelseledamot: ['boende', 'medlem', 'styrelseledamot', 'ekonomi'],
  förvaltare: ['styrelseledamot', 'ekonomi'],
  kassör: ['ekonomi'],
  ekonomiassistent: ['ekonomi'],
  organisation_admin: [
    'boende',
    'medlem',
    'anställd',
    // Administratören ser menyposten för att kunna felsöka vyn, men ser bara sina EGNA barn:
    // MinaBarnController går via hushållsnyckeln och inte via rollen. Vägen till alla barn är
    // Barnregister under Förskola.
    'vårdnadshavare',
    'styrelseledamot',
    'organisation_admin',
    'ekonomi',
  ],
  egenföretagare: ['egenföretagare', 'ekonomi'],
  aktiebolagsägare: ['egenföretagare', 'ekonomi'],
  arborist: ['arborist'],
  // Revisorn når föreningens egna ekonomivyer för att kunna granska dem, men bara läsande:
  // `RevisorsSkrivspärrAspekt` i backend avvisar allt som inte är GET utanför /api/revision.
  // Ingen annan roll ger 'revisor' — Revision-menyn är avsedd för just den som är vald revisor.
  revisor: ['revisor', 'ekonomi'],
  // Flinkys personal hjälper kunder, men får inga kundroller av det: ingen föreningsdata.
  flinky_handläggare: ['flinky_handläggare'],
  super_admin: [
    'boende',
    'medlem',
    'anställd',
    'vårdnadshavare',
    'styrelseledamot',
    'organisation_admin',
    'ekonomi',
    'egenföretagare',
    'arborist',
    'revisor',
    'flinky_handläggare',
    'super_admin',
  ],
};

// ----------------------------------------------------------------------

/**
 * Ger föräldrar utan egen `allowedRoles` unionen av barnens. Poängen är att en
 * gren aldrig ska kunna bli oåtkomlig för att någon glömt uppdatera föräldern
 * när ett barn öppnats för fler roller.
 *
 * Sätter man `allowedRoles` på en förälder blir den i stället en hård grind:
 * barnen filtreras bara om föräldern släpper igenom. Använd det medvetet, t.ex.
 * på administrationsmenyerna.
 */
export function härledBehörigheter(items: NavPost[]): NavPost[] {
  return items.map((item) => {
    if (!item.children?.length) {
      return item;
    }

    const barn = härledBehörigheter(item.children);

    if (item.allowedRoles?.length) {
      return { ...item, children: barn };
    }

    const union = new Set<Behörighet>();
    barn.forEach((b) => b.allowedRoles?.forEach((roll) => union.add(roll)));

    return { ...item, allowedRoles: [...union], children: barn };
  });
}

/**
 * Toppnivåmenyerna som ingår i lanseringsytan.
 *
 * Produkten har fler moduler än den har färdiga moduler. Den här uppsättningen
 * är en **allowlist**: en nyckel som inte står här är av som standard, och en ny
 * modul som läggs till i trädet är därmed parkerad tills någon skriver in den.
 * Motsatsen — `standardDold` på varje parkerad post — hade varit en denylist, där
 * nästa modul blir synlig utan att någon valt det.
 *
 * Uppsättningen är ett *standardvärde*, inte en spärr. En organisation som sparat
 * `synlig: true` för en nyckel får den ändå — det är så ekonomigrenen är påslagen
 * för de organisationer som gör sin bokföring här, medan den ligger av för en
 * nyfiken förening.
 *
 * Avvecklingen är att plocka ut nycklar i takt med att moduler blir klara, och
 * till slut tömma uppsättningen: `size === 0` ger exakt beteendet före lanseringen.
 */
export const LANSERINGSUPPSÄTTNING: ReadonlySet<string> = new Set<string>([
  'kom-igang',

  // Meddelanden, kanaler och utskick — föreningens kommunikation.
  'meddelanden',
  'utskick',

  // Utlåningspoolen, kroken i första säljsamtalet.
  'boka-lana',
  'utlaningspool-mina',
  'utlaningspool-inkop',
  'utlaningspool-admin',

  // Medlemsregistret — men inte `medlemmar-avgifter` eller `medlemmar-avier`,
  // som drar in räkenskapsår, bokföringskonto och bank.
  'medlemmar',

  // Styrelsens möten, ledamöter och arkiv. Hård grind mot styrelseroller, så
  // boende ser den inte.
  'styrelse',

  // Stadgar, grunduppgifter och kontakter — men inte `foreningen-arsredovisning`,
  // som länkar in i ekonomigrenen.
  'foreningen',
  'dokument',

  // Måste vara med: det är här en organisation slår på allt det andra.
  'installningar',
]);

/**
 * Utgångsläget för en meny när organisationen inte sparat något val.
 *
 * `standardDold` och lanseringsuppsättningen uttrycker olika saker och vägs
 * därför samman med `&&`: `standardDold` betyder "den här modulen angår bara
 * vissa slags organisationer" (förskola, arborist, offentlighetsprincipen),
 * medan uppsättningen betyder "inte färdig än". En nyckel som är dold av
 * verksamhetsskäl ska inte bli synlig bara för att den råkar ligga i v1-listan.
 */
export function standardSynlighetFör(menyNyckel?: string, standardDold = false): boolean {
  if (!menyNyckel) {
    return true;
  }

  if (LANSERINGSUPPSÄTTNING.size === 0) {
    return !standardDold;
  }

  return LANSERINGSUPPSÄTTNING.has(menyNyckel) && !standardDold;
}

/**
 * Om menyn är dold <em>enbart</em> för att lanseringsuppsättningen håller den stängd.
 *
 * Skilt från `standardDold`, och skillnaden är hela poängen: `standardDold` betyder att
 * modulen bara angår vissa slags organisationer och alltså förblir dold, medan en parkerad
 * nyckel är en modul som finns men inte är påslagen ännu. En admin som ser att ekonomin är
 * avstängd ska kunna se vilket av de två det är — det första är ett besked, det andra en
 * knapp hen själv kan trycka på.
 */
export function ärParkeradTillLansering(menyNyckel?: string, standardDold = false): boolean {
  if (!menyNyckel || standardDold || LANSERINGSUPPSÄTTNING.size === 0) {
    return false;
  }

  return !LANSERINGSUPPSÄTTNING.has(menyNyckel);
}

/**
 * Om en meny ska visas för organisationen.
 *
 * Saknar posten nyckel är den alltid synlig. Finns en sparad inställning styr
 * den. Saknas inställningen gäller {@link standardSynlighetFör}.
 */
export function ärMenySynlig(
  menyNyckel: string | undefined,
  menySynlighet?: Record<string, boolean>,
  standardDold = false
): boolean {
  if (!menyNyckel) {
    return true;
  }

  const sparad = menySynlighet?.[menyNyckel];

  return sparad === undefined ? standardSynlighetFör(menyNyckel, standardDold) : sparad !== false;
}

function harBehörighet(allowedRoles: string[] | undefined, effektiva: Set<string>): boolean {
  if (allowedRoles?.some((roll) => roll === 'alla')) {
    return true;
  }
  if (!allowedRoles?.length) {
    return false;
  }
  return allowedRoles.some((roll) => effektiva.has(roll.toLowerCase()));
}

/**
 * Expanderar en lista roller till alla behörighetsgrupper de ger.
 *
 * Tar `unknown` därför att rollernas form varierar mellan kodvägar: vissa
 * levererar objekt med `namn`, andra med `rollNamn`, och några rena strängar.
 * Den varianten var orsaken till en tyst bugg i {@link ärOrganisationsadmin} —
 * en jämförelse mot objektet i stället för namnet returnerar alltid falskt.
 */
export function expanderaRoller(roller: unknown): Set<string> {
  const behörigheter = new Set<string>();

  if (!Array.isArray(roller)) {
    return behörigheter;
  }

  for (const roll of roller) {
    const rollNamn = String(
      (roll as any)?.rollNamn ?? (roll as any)?.namn ?? roll ?? ''
    ).toLowerCase();
    if (!rollNamn) continue;
    behörigheter.add(rollNamn);
    ROLL_TILL_BEHÖRIGHETER[rollNamn]?.forEach((b) => behörigheter.add(b));
  }

  return behörigheter;
}

/**
 * Sant om rollerna ger den efterfrågade behörighetsgruppen.
 *
 * Det här är den enda källan till sanning för "får den här personen göra X" i
 * gränssnittet. Funktioner som bygger egna rollistor hamnar förr eller senare i
 * otakt med {@link ROLL_TILL_BEHÖRIGHETER} — vilket är precis vad som hände i
 * utlåningspoolen, där sidomenyn släppte in en styrelseledamot på en sida där
 * varje knapp sedan var admin-spärrad.
 */
export function harBehörighetsgrupp(roller: unknown, grupp: Behörighet): boolean {
  return expanderaRoller(roller).has(grupp);
}

/**
 * Expanderar användarens roller till alla behörighetsgrupper de ger.
 */
function effektivaBehörigheter(anvandare: IAnvändare): Set<string> {
  return expanderaRoller(anvandare.roller);
}

export const ROLLER_FÖR_MENYINSTÄLLNINGAR: { kod: Behörighet; etikett: string }[] = [
  { kod: 'alla', etikett: 'Alla' },
  { kod: 'boende', etikett: 'Boende & Medlemmar' },
  { kod: 'styrelseledamot', etikett: 'Styrelseledamöter' },
  { kod: 'ekonomi', etikett: 'Ekonomiansvariga' },
  { kod: 'organisation_admin', etikett: 'Organisationsadmin' },
  { kod: 'egenföretagare', etikett: 'Konsulter & Egenföretagare' },
  { kod: 'arborist', etikett: 'Arborist' },
];

export type MenyInställningObjekt = {
  synlig: boolean;
  roller?: string[];
};

export type MenyInställningInput = Record<string, boolean | MenyInställningObjekt>;

function filtreraPost(
  item: NavItemDataProps,
  effektiva: Set<string>,
  menySynlighet?: MenyInställningInput
): NavItemDataProps | null {
  const menyVal = item.menyNyckel ? menySynlighet?.[item.menyNyckel] : undefined;
  
  let synlig = true;
  let tillåtnaRoller = item.allowedRoles;

  if (typeof menyVal === 'boolean') {
    synlig = menyVal;
  } else if (menyVal && typeof menyVal === 'object') {
    synlig = menyVal.synlig !== false;
    if (menyVal.roller && menyVal.roller.length > 0) {
      tillåtnaRoller = menyVal.roller;
    }
  } else {
    synlig = ärMenySynlig(item.menyNyckel, undefined, item.standardDold);
  }

  if (!synlig) {
    return null;
  }

  if (!harBehörighet(tillåtnaRoller, effektiva)) {
    return null;
  }

  const nyPost = { ...item };

  if (nyPost.children) {
    const kvarvarande = nyPost.children
      .map((barn) => filtreraPost(barn, effektiva, menySynlighet))
      .filter((barn): barn is NavItemDataProps => barn !== null);

    if (kvarvarande.length > 0) {
      nyPost.children = kvarvarande;
    } else if (!nyPost.path) {
      // Ren mapp utan kvarvarande innehåll — dölj den.
      return null;
    } else {
      // Egen länk kvar: behåll den som löv.
      delete nyPost.children;
    }
  }

  return nyPost;
}

/**
 * Behörighetskontrollen som nav-komponenterna får via `checkPermissions`.
 *
 * Nav-komponenterna filtrerar posterna en gång till när de renderar, ovanpå
 * {@link filtreraNavigering}. Den kontrollen måste använda exakt samma
 * rollexpansion — gör den en rå jämförelse mot rollnamnen försvinner varje post
 * vars `allowedRoles` är en behörighetsgrupp (`boende`, `ekonomi`) i stället för
 * ett rollnamn.
 */
export function skapaBehörighetskontroll(
  anvandare: IAnvändare | undefined
): (allowedRoles?: string[]) => boolean {
  const effektiva = anvandare ? effektivaBehörigheter(anvandare) : new Set<string>();
  return (allowedRoles) => harBehörighet(allowedRoles, effektiva);
}

/**
 * Filtrerar navigeringsträdet mot användarens roller och organisationens
 * menyinställningar. Tomma sektioner faller bort.
 */
export function filtreraNavigering(
  data: NavSectionProps['data'],
  anvandare: IAnvändare | undefined,
  menySynlighet?: MenyInställningInput
): NavGroupProps[] {
  if (!anvandare?.namn) {
    return [];
  }

  const effektiva = effektivaBehörigheter(anvandare);

  return data
    .map((grupp) => {
      const poster = grupp.items
        .map((item) => filtreraPost(item, effektiva, menySynlighet))
        .filter((item): item is NavItemDataProps => item !== null);

      return poster.length > 0 ? { ...grupp, items: poster } : null;
    })
    .filter((grupp): grupp is NavGroupProps => grupp !== null);
}

// ----------------------------------------------------------------------

export type MenyKatalogPost = {
  nyckel: string;
  etikett: string;
  sektion: string;
  /** Utgångsläget när organisationen inte sparat något val för menyn. */
  standardSynlig: boolean;
  /** Dold bara tills modulen släpps — se {@link ärParkeradTillLansering}. */
  parkerad: boolean;
  standardRoller: Behörighet[];
};

export type MenyKatalogNod = MenyKatalogPost & {
  barn?: MenyKatalogNod[];
};

/**
 * Bygger en hierarkisk struktur av menykatalogen ur trädet.
 */
export function byggMenyTräd(data: NavSectionProps['data']): MenyKatalogNod[] {
  const samlaNod = (item: NavItemDataProps, sektion: string): MenyKatalogNod | null => {
    const barn = (item.children ?? [])
      .map((child) => samlaNod(child, sektion))
      .filter((child): child is MenyKatalogNod => child !== null);

    if (!item.menyNyckel && barn.length === 0) {
      return null;
    }

    // Om posten saknar egen nyckel men har barn med nycklar (t.ex. en gruppmapp),
    // kan vi använda en tillfällig nyckel eller returnera barnen.
    if (item.menyNyckel) {
      return {
        nyckel: item.menyNyckel,
        etikett: item.title,
        sektion,
        standardSynlig: standardSynlighetFör(item.menyNyckel, item.standardDold),
        parkerad: ärParkeradTillLansering(item.menyNyckel, item.standardDold),
        standardRoller: (item.allowedRoles as Behörighet[]) ?? [],
        ...(barn.length > 0 ? { barn } : {}),
      };
    }

    return null;
  };

  return data.flatMap((grupp) => {
    const sektion = grupp.subheader ?? 'Allmänt';
    return grupp.items
      .map((item) => samlaNod(item, sektion))
      .filter((nod): nod is MenyKatalogNod => nod !== null);
  });
}

/**
 * Härleder katalogen över av/på-bara menyer direkt ur trädet i stället för att
 * upprepa den i en handunderhållen lista. Sektionen är gruppens rubrik; poster i
 * den rubriklösa startgruppen hamnar under "Allmänt".
 */
export function byggMenyKatalog(data: NavSectionProps['data']): MenyKatalogPost[] {
  const platta = (noder: MenyKatalogNod[]): MenyKatalogPost[] =>
    noder.flatMap((nod) => [
      {
        nyckel: nod.nyckel,
        etikett: nod.etikett,
        sektion: nod.sektion,
        standardSynlig: nod.standardSynlig,
        parkerad: nod.parkerad,
        standardRoller: nod.standardRoller,
      },
      ...platta(nod.barn ?? []),
    ]);

  return platta(byggMenyTräd(data));
}

// ----------------------------------------------------------------------

/** Trädet har både `/dokument` och `/ekonomi/bokforing/handelser/` — jämför utan avslutande snedstreck. */
function utanSlutSnedstreck(sökväg: string): string {
  return sökväg.length > 1 ? sökväg.replace(/\/+$/, '') : sökväg;
}

/**
 * Alla sökvägar i ett navigeringsträd, platt och avdubblerade.
 *
 * Finns för ruttspärren, som behöver jämföra hela trädet mot det filtrerade för
 * att veta vad som är parkerat. Att härleda båda ur samma trädgenomgång är det
 * som håller spärren i takt med menyn — en egen lista hade glidit isär.
 */
export function plattaNavSökvägar(data: NavSectionProps['data']): string[] {
  const samla = (items: NavItemDataProps[]): string[] =>
    items.flatMap((item) => [
      ...(item.path ? [utanSlutSnedstreck(item.path)] : []),
      ...samla(item.children ?? []),
    ]);

  return [...new Set(data.flatMap((grupp) => samla(grupp.items)))];
}

/**
 * Den navsökväg en pathname hör till — längsta segmentmatchningen, eller
 * `undefined` när ingen post i trädet äger sökvägen.
 *
 * Längsta matchning är rätt regel och inte en optimering: `Bygglov` ligger under
 * `Projekt` men pekar på `/fastighet/mina-hem/bygglov`, och ska träffa sin egen
 * post och inte `Fastighet`. Matchningen går på hela segment, så `/medlemmar`
 * aldrig träffar `/medlemmarXYZ`.
 *
 * `undefined` betyder "trädet äger inte den här sidan" — kontosidor, callbacks,
 * delningslänkar och detaljrutter. De ska släppas igenom, inte spärras.
 */
export function lösNavSökväg(
  pathname: string,
  navSökvägar: Iterable<string>
): string | undefined {
  const sökt = utanSlutSnedstreck(pathname);
  let bäst: string | undefined;

  for (const sökväg of navSökvägar) {
    const kandidat = utanSlutSnedstreck(sökväg);
    const träff = sökt === kandidat || sökt.startsWith(`${kandidat}/`);

    if (träff && (bäst === undefined || kandidat.length > bäst.length)) {
      bäst = kandidat;
    }
  }

  return bäst;
}
